<template>
  <ul class="app-sidebar-menu">
    <li v-for="item in displayItems" :key="item.id" class="app-sidebar-menu-list">
      <div
        class="cursor-pointer"
        :class="{
          'app-sidebar-menu-list-item': true,
          group: true,
          active: item.active,
        }"
        :data-testid="`nav-${item.id}`"
        :data-drawer-hide="item.link ? sideMenuId : undefined"
        @click.prevent="($event) => handleItemClick(item, $event)"
      >
        <component :is="item.icon" v-if="item.icon" class="app-sidebar-menu-list-icon" />
        <div class="grow flex justify-between items-center">
          <span class="ms-3">{{ item.name }}</span>
          <svg
            v-if="item.children"
            class="w-3 h-3 dark:text-icon dark:group-hover:text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 10 6"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 4 4 4-4"
            />
          </svg>
        </div>
      </div>
      <ul v-if="item.children" :class="`${item.expanded ? '' : 'hidden'}`">
        <li v-for="subItem in item.children" :key="subItem.id">
          <div
            class="cursor-pointer"
            :data-testid="`nav-${subItem.id}`"
            :data-drawer-hide="subItem.link ? sideMenuId : undefined"
            :class="{
              'app-sidebar-menu-dropdown-list-item': true,
              group: true,
              active: subItem.active,
            }"
            @click.prevent="($event) => handleItemClick(subItem, $event)"
          >
            <div class="flex justify-between items-center w-full">
              <span>{{ subItem.name }}</span>
              <svg
                v-if="subItem.children"
                class="w-3 h-3 dark:text-icon-support dark:group-hover:text-white trasition-all duration-200"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 10 6"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 4 4 4-4"
                />
              </svg>
            </div>
          </div>
          <ul v-if="subItem.children" :class="`${subItem.expanded ? '' : 'hidden'}`">
            <li v-for="grandChild in subItem.children" :key="grandChild.id">
              <div
                class="!pl-14 cursor-pointer"
                :data-testid="`nav-${grandChild.id}`"
                :data-drawer-hide="`${grandChild.link ? sideMenuId : undefined}`"
                :class="{
                  'app-sidebar-menu-dropdown-list-item': true, //Use same class as the subItem level
                  group: true,
                  active: grandChild.active,
                }"
                @click.prevent="($event) => handleItemClick(grandChild, $event)"
              >
                {{ grandChild.name }}
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router';
import { SideMenuItem } from '@/entities/sideMenuItem.entity';
import { nextTick, onMounted, ref, watchEffect } from 'vue';
import { initDrawers } from 'flowbite';

const props = defineProps<{
  items: SideMenuItem[];
  sideMenuId: string;
}>();

const router = useRouter();

const displayItems = ref<SideMenuItem[]>(props.items);

const setActiveItem = (items: SideMenuItem[], path: string, query: Record<string, unknown>): boolean => {
  let hasActiveItem = false;
  for (const item of items) {
    // Reset active state first
    item.active = false;

    if (item.children) {
      const hasActiveChild = setActiveItem(item.children, path, query);
      item.expanded = hasActiveChild; // Expand parent if any child is active
      if (hasActiveChild) {
        hasActiveItem = true;
      }
    } else if (item.link) {
      const [itemPath, itemQueryString] = item.link.split('?');
      const itemQuery = itemQueryString ? parseQueryString(itemQueryString) : {};

      // Check both path and query parameters
      const pathMatches = path === itemPath;
      const queryMatches =
        Object.keys(itemQuery).length > 0 ? Object.keys(itemQuery).every((key) => itemQuery[key] === query[key]) : true;

      if (pathMatches && queryMatches) {
        item.active = true;
        hasActiveItem = true;
      }
    }
  }
  return hasActiveItem;
};

const parseQueryString = (queryString: string): Record<string, string> => {
  const query: Record<string, string> = {};
  const pairs = (queryString.startsWith('?') ? queryString.substring(1) : queryString).split('&');

  for (const element of pairs) {
    const pair = element.split('=');
    query[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1] || '');
  }
  return query;
};

watchEffect(() => {
  displayItems.value = props.items;
  const currentPath = router.currentRoute.value.path;
  const currentQuery = router.currentRoute.value.query;
  setActiveItem(displayItems.value, currentPath, currentQuery);
});

const handleItemClick = (item: SideMenuItem, $event: MouseEvent): void => {
  $event.preventDefault();

  if (item.children && item.children.length > 0) {
    // Toggle expanded state if it has children
    item.expanded = !item.expanded;
  } else if (item.link) {
    const [path, queryString] = item.link.split('?');
    const query = queryString ? parseQueryString(queryString) : {};

    router.push({ path, query });
  }
};

onMounted(() => {
  nextTick(() => {
    initDrawers();
  });
});
</script>
